import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Link, Outlet } from "react-router-dom";
import { Eye } from "lucide-react";
import {
  PATIENT_SESSION_REVOCATION_REASONS,
  type PatientSessionRevocationReason,
} from "@care-by-ella/contracts";
import { setSupportReasonPrompt } from "@/lib/api";
import { SESSION_REVOCATION_REASON_LABEL } from "@/lib/patient-sessions";
import {
  fetchSupportAccessStatus,
  grantSupportAccess,
  revokePatientSessions,
} from "@/lib/support-access-api";
import { UnauthorizedError } from "@/lib/clinician-api";
import { useAuth } from "@/auth/AuthContext";
import {
  dedupePrompt,
  ensureSupportAccess,
  formatAccessUntil,
  inView,
  isSupportReasonValid,
  SUPPORT_REASON_MAX,
  SUPPORT_REASON_MIN,
  type ReasonPrompt,
  type SupportView,
} from "@/lib/support-view";

// TES-1040 — the admin's read-only support views.
//
// An admin reads the doctor's and the coach's pages exactly as they render
// for them, inside SupportViewLayout. The context it provides is what every
// page and panel asks:
//   - useReadOnly(): hide every control that changes clinical or patient
//     state (hidden, not disabled: a greyed "Skicka" still reads as an
//     action someone is expected to take);
//   - useViewPath(): keep in-app links inside the section the admin opened;
//   - requestReason(): the per-patient PDL reason dialog.
//
// For a doctor or a coach the context is null and nothing changes. The
// server enforces all of it regardless: admin holds GET routes only.

export interface SupportViewValue {
  viewAs: SupportView;
  /** Shows the reason dialog for a patient; true once access is granted. */
  requestReason: ReasonPrompt;
  /** When today's access to this patient ends (ISO), if known. */
  accessUntil: (patientId: string) => string | null;
  /** Records a grant's end, from a status read or a fresh grant. */
  noteAccess: (patientId: string, expiresAt: string | null) => void;
}

const SupportViewContext = createContext<SupportViewValue | null>(null);

/** The support view this page renders in, or null for a doctor or coach. */
export function useSupportView(): SupportViewValue | null {
  return useContext(SupportViewContext);
}

/** True in the admin's support views: hide every mutating control. */
export function useReadOnly(): boolean {
  return useContext(SupportViewContext) !== null;
}

/**
 * For a component that is nothing but an action (a correction form, a
 * handover form, an AI button): renders nothing at all in a support view.
 */
export function hiddenWhenReadOnly<P extends object>(
  Component: ComponentType<P>,
): ComponentType<P> {
  function HiddenWhenReadOnly(props: P) {
    return useReadOnly() ? null : <Component {...props} />;
  }
  HiddenWhenReadOnly.displayName = `HiddenWhenReadOnly(${
    Component.displayName ?? Component.name
  })`;
  return HiddenWhenReadOnly;
}

/** An in-app path as this view spells it (prefixed in a support view). */
export function useViewPath(): (path: string) => string {
  const view = useContext(SupportViewContext)?.viewAs ?? null;
  return useCallback((path: string) => inView(view, path), [view]);
}

const BANNER_ROLE: Record<SupportView, string> = {
  doctor: "läkarens",
  coach: "coachens",
};

/**
 * The reason dialog, registered with the API client for as long as the
 * calling component is mounted, so a 403 `support_reason_required` on a read
 * opens it (createSupportFetch). Returns the prompt and the dialog to render.
 */
function useReasonDialog(
  onGranted?: (patientId: string, expiresAt: string) => void,
): { requestReason: ReasonPrompt; dialog: ReactNode } {
  const [pending, setPending] = useState<{
    patientId: string;
    resolve: (granted: boolean) => void;
  } | null>(null);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const onGrantedRef = useRef(onGranted);
  onGrantedRef.current = onGranted;

  const requestReason = useMemo(
    () =>
      dedupePrompt(
        (patientId) =>
          new Promise<boolean>((resolve) => {
            // One patient page is open at a time; a prompt for another
            // patient while one is showing is a stale page's read.
            if (pendingRef.current) {
              resolve(false);
              return;
            }
            setPending({ patientId, resolve });
          }),
      ),
    [],
  );

  // Registered before any child's effects fetch: layout effects run before
  // passive ones, so the first read of a page already sees the prompt.
  useLayoutEffect(() => {
    setSupportReasonPrompt(requestReason);
    return () => setSupportReasonPrompt(null);
  }, [requestReason]);

  function settle(granted: boolean, expiresAt?: string) {
    const p = pendingRef.current;
    if (!p) return;
    if (granted && expiresAt) onGrantedRef.current?.(p.patientId, expiresAt);
    setPending(null);
    p.resolve(granted);
  }

  const dialog = pending ? (
    <SupportReasonDialog
      key={pending.patientId}
      patientId={pending.patientId}
      onGranted={(expiresAt) => settle(true, expiresAt)}
      onCancel={() => settle(false)}
    />
  ) : null;
  return { requestReason, dialog };
}

/** The route element for `/support/doctor` and `/support/coach`. */
export function SupportViewLayout({ viewAs }: { viewAs: SupportView }) {
  const [grants, setGrants] = useState<Record<string, string | null>>({});

  const noteAccess = useCallback(
    (patientId: string, expiresAt: string | null) =>
      setGrants((g) =>
        g[patientId] === expiresAt ? g : { ...g, [patientId]: expiresAt },
      ),
    [],
  );
  const { requestReason, dialog } = useReasonDialog(noteAccess);

  const value = useMemo<SupportViewValue>(
    () => ({
      viewAs,
      requestReason,
      accessUntil: (patientId) => grants[patientId] ?? null,
      noteAccess,
    }),
    [viewAs, requestReason, grants, noteAccess],
  );

  return (
    <SupportViewContext.Provider value={value}>
      <div className="flex flex-col gap-lg">
        <div
          role="status"
          className="flex items-center gap-sm rounded-md border border-warning/40 bg-warning/10 px-md py-sm text-sm text-textPrimary"
        >
          <Eye size={16} strokeWidth={1.75} aria-hidden="true" />
          <span>
            Läsläge – du ser {BANNER_ROLE[viewAs]} vy som admin. Inga ändringar
            kan göras.
          </span>
        </div>
        {/* Keyed on the view: switching between the two sections remounts
            the pages, so nothing fetched in one shape lingers in the other. */}
        <Outlet key={viewAs} />
      </div>
      {dialog}
    </SupportViewContext.Provider>
  );
}

/**
 * TES-1123 — the coach's own patient page, for a coach who also holds the
 * admin grant (TES-1089). Opening a patient she has no coaching basis for
 * (no trialing or active subscription, no earlier relationship), the API
 * treats her as the admin she also is and answers 403
 * `support_reason_required`; this host registers the same reason dialog, so
 * the page's reads ask for a reason and retry instead of failing. She stays in
 * the coach's view, with its controls. A pure coach never gets that 403, so
 * nothing is registered for her.
 */
function CoachReasonPromptHost({ children }: { children: ReactNode }) {
  const { dialog } = useReasonDialog();
  return (
    <>
      {children}
      {dialog}
    </>
  );
}

export function CoachAdminReasonPrompt({ children }: { children: ReactNode }) {
  const { claims } = useAuth();
  const coachAdmin =
    claims?.role === "coach" && Boolean(claims.roles?.includes("admin"));
  if (!coachAdmin) return <>{children}</>;
  return <CoachReasonPromptHost>{children}</CoachReasonPromptHost>;
}

export function SupportReasonDialog({
  patientId,
  onGranted,
  onCancel,
}: {
  patientId: string;
  onGranted: (expiresAt: string) => void;
  onCancel: () => void;
}) {
  const { signOut } = useAuth();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    fieldRef.current?.focus();
  }, []);

  const valid = isSupportReasonValid(reason);

  async function submit() {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { expiresAt } = await grantSupportAccess(patientId, {
        reason: reason.trim(),
      });
      onGranted(expiresAt);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(
        e instanceof Error && e.message.includes("reason_invalid")
          ? `Orsaken ska vara ${SUPPORT_REASON_MIN}–${SUPPORT_REASON_MAX} tecken.`
          : "Kunde inte spara orsaken. Försök igen.",
      );
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-md z-50">
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-reason-title"
        aria-describedby="support-reason-help"
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
        }}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="bg-surface rounded-md p-lg max-w-md w-full flex flex-col gap-md"
      >
        <h3 id="support-reason-title" className="font-display text-xl">
          Ange orsak för åtkomst
        </h3>
        <p id="support-reason-help" className="text-textSecondary text-sm">
          Orsaken loggas i åtkomstgranskningen och gäller den här patienten
          resten av dagen.
        </p>
        <label className="flex flex-col gap-xs text-sm">
          <span className="sr-only">Orsak</span>
          <textarea
            ref={fieldRef}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={SUPPORT_REASON_MAX}
            rows={3}
            placeholder="T.ex. supportärende eller felrapport"
            className="rounded-md border border-surfaceMuted bg-background p-sm text-sm"
          />
          <span className="text-xs text-textSecondary">
            {SUPPORT_REASON_MIN}–{SUPPORT_REASON_MAX} tecken
          </span>
        </label>
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
        <div className="flex gap-sm justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-md py-sm rounded-md text-textSecondary hover:bg-surfaceMuted text-sm"
          >
            Avbryt
          </button>
          <button
            type="submit"
            disabled={!valid || busy}
            className="px-md py-sm rounded-md bg-brand text-white text-sm hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Sparar…" : "Öppna patienten"}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Wraps a patient page in a support view: checks today's grant before the
 * page mounts (so none of its reads fires without one) and asks for a
 * reason when there is none. Outside a support view it renders its children
 * untouched.
 */
export function SupportAccessGate({
  patientId,
  children,
}: {
  patientId: string;
  children: ReactNode;
}) {
  const support = useSupportView();
  const { signOut } = useAuth();
  const viewPath = useViewPath();
  const [state, setState] = useState<
    "checking" | "granted" | "cancelled" | "error"
  >("checking");

  const requestReason = support?.requestReason;
  const noteAccess = support?.noteAccess;
  useEffect(() => {
    if (!requestReason || !noteAccess) return;
    let live = true;
    setState("checking");
    ensureSupportAccess(
      patientId,
      {
        status: async (id) => {
          const s = await fetchSupportAccessStatus(id);
          if (s.active) noteAccess(id, s.expiresAt);
          return s;
        },
      },
      requestReason,
    )
      .then((ok) => {
        if (live) setState(ok ? "granted" : "cancelled");
      })
      .catch((e: unknown) => {
        if (e instanceof UnauthorizedError) {
          void signOut();
          return;
        }
        if (live) setState("error");
      });
    return () => {
      live = false;
    };
  }, [patientId, requestReason, noteAccess, signOut]);

  if (!support) return <>{children}</>;
  if (state === "granted") return <>{children}</>;
  if (state === "checking") {
    return <p className="text-sm text-textSecondary">Hämtar…</p>;
  }
  return (
    <div className="flex flex-col gap-sm items-start">
      <p className="text-sm text-textSecondary">
        {state === "cancelled"
          ? "Patienten öppnades inte. En orsak krävs för att se patientens uppgifter."
          : "Kunde inte kontrollera åtkomsten till patienten."}
      </p>
      <Link
        to={viewPath("/patients")}
        className="text-sm text-brand hover:underline"
      >
        ← Till patientlistan
      </Link>
    </div>
  );
}

/** "Åtkomst gäller till HH:MM", in a support view only. */
export function SupportAccessUntil({ patientId }: { patientId: string }) {
  const until = useSupportView()?.accessUntil(patientId) ?? null;
  if (!until) return null;
  return <span>Åtkomst gäller till {formatAccessUntil(until)}</span>;
}

/**
 * TES-1205 — "Logga ut patienten från alla enheter", in a support view only.
 *
 * The one support control in this header that changes anything, and only in
 * the patient's favour: every app session she has ends (a lost or stolen
 * phone) and she signs in again with BankID. It touches no clinical state.
 * Like her reads it needs today's support reason (the page already holds
 * one), and the action is audited with a reason picked from a fixed list.
 */
export function SignOutPatientEverywhere({ patientId }: { patientId: string }) {
  const support = useSupportView();
  const [open, setOpen] = useState(false);
  const [doneAt, setDoneAt] = useState<string | null>(null);
  if (!support) return null;
  if (doneAt) {
    return (
      <span role="status">
        Utloggad från alla enheter {formatAccessUntil(doneAt)}
      </span>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-brand hover:underline"
      >
        Logga ut patienten från alla enheter
      </button>
      {/* A portal: the button sits in the header's line of text (a <p>),
          where the dialog's block markup cannot. */}
      {open
        ? createPortal(
            <SignOutEverywhereDialog
              patientId={patientId}
              onDone={(at) => {
                setOpen(false);
                setDoneAt(at);
              }}
              onCancel={() => setOpen(false)}
            />,
            document.body,
          )
        : null}
    </>
  );
}

function SignOutEverywhereDialog({
  patientId,
  onDone,
  onCancel,
}: {
  patientId: string;
  onDone: (sessionsValidAfter: string) => void;
  onCancel: () => void;
}) {
  const { signOut } = useAuth();
  const [reason, setReason] = useState<PatientSessionRevocationReason | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!reason || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { sessionsValidAfter } = await revokePatientSessions(patientId, {
        reason,
      });
      onDone(sessionsValidAfter);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError("Kunde inte logga ut patienten. Försök igen.");
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-md z-50">
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="signout-everywhere-title"
        aria-describedby="signout-everywhere-help"
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
        }}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="bg-surface rounded-md p-lg max-w-md w-full flex flex-col gap-md"
      >
        <h3 id="signout-everywhere-title" className="font-display text-xl">
          Logga ut patienten från alla enheter?
        </h3>
        <p id="signout-everywhere-help" className="text-textSecondary text-sm">
          Patienten loggas ut i appen på alla enheter och loggar in igen med
          BankID. Åtgärden och orsaken loggas.
        </p>
        <fieldset className="flex flex-col gap-xs text-sm">
          <legend className="mb-xs">Orsak</legend>
          {PATIENT_SESSION_REVOCATION_REASONS.map((r) => (
            <label key={r} className="flex items-center gap-sm">
              <input
                type="radio"
                name="signout-everywhere-reason"
                value={r}
                checked={reason === r}
                onChange={() => setReason(r)}
              />
              {SESSION_REVOCATION_REASON_LABEL[r]}
            </label>
          ))}
        </fieldset>
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
        <div className="flex gap-sm justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-md py-sm rounded-md text-textSecondary hover:bg-surfaceMuted text-sm"
          >
            Avbryt
          </button>
          <button
            type="submit"
            disabled={!reason || busy}
            className="px-md py-sm rounded-md bg-danger text-white text-sm hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Loggar ut…" : "Logga ut patienten"}
          </button>
        </div>
      </form>
    </div>
  );
}
